import React from "react";
import { createGlobalStyle } from "styled-components";
import PreflightStyles from "./preflight";

// Global typography — replaces what dskit-prototype.css used to provide
// before the teardown (PR #464). Without these, browsers fall back to
// serif body text (Times) and underlined links.
//
// CircularStd is bundled in public/fonts/circular-std/ (woff2 only — modern
// browsers, ~28KB each gzipped × 4 weights = ~110KB raw / ~40KB gz total).
// Used by BoxForm primitives and a handful of screens for headings/buttons.
const CustomStyles = createGlobalStyle`
  @font-face {
    font-family: "CircularStd";
    src: url("/fonts/circular-std/CircularStd-Book.woff2") format("woff2");
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "CircularStd";
    src: url("/fonts/circular-std/CircularStd-Medium.woff2") format("woff2");
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "CircularStd";
    src: url("/fonts/circular-std/CircularStd-Bold.woff2") format("woff2");
    font-weight: 600;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "CircularStd";
    src: url("/fonts/circular-std/CircularStd-Black.woff2") format("woff2");
    font-weight: 800;
    font-style: normal;
    font-display: swap;
  }

  /* Bootstrap/dskit set this globally — without it, widths exclude
     padding (browser default content-box) and Container/Card sizing
     calculations break. Symptom: dashboard event grid showed 2
     columns instead of 3. */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  body {
    /* Bootstrap reboot — without margin:0 the body has the default 8px
       UA margin, which renders as a visible white "border" between the
       site bg and the viewport. */
    margin: 0;
    transition: none;
    font-family: "Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      "Helvetica Neue", Arial, sans-serif;
    color: #3e3f42;
    background: #fbfbfd;
    font-size: 1em;
    line-height: 1.5em;
    -webkit-font-smoothing: antialiased;
  }

  /* Bootstrap reboot for headings — without margin-top:0 + margin-bottom:0.5rem,
     browsers apply ~1em top + bottom margin to h1-h6, which combined with our
     SectionHeading my-[30px] wrapper made the Dashboard heading area huge. Same
     pattern caused EventCard restaurant-name (UnderlinedTitle = styled.h3) to
     have too much padding below. */
  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
    margin-bottom: 0.5rem;
  }

  /* Bootstrap reboot for paragraphs */
  p {
    margin-top: 0;
    margin-bottom: 1rem;
  }

  a {
    color: #1665d8;
    text-decoration: none;
    transition: 0.3s all cubic-bezier(0.6, 0.6, 0.7, 1.5);
  }

  a:hover {
    color: rgb(17, 79, 169);
    text-decoration: none;
  }

  /* Heading defaults from dskit. Without these, browsers fall back to
     small user-agent defaults (h4 ~1.17em vs dskit's 1.75rem etc.) and
     primitives like BoxHeadline (styled.h4) render too small. */
  h1 { font-size: 4rem; font-weight: 400; }
  h2 { font-size: 2.5em; font-weight: 300; margin-bottom: 0; }
  h3 { font-size: 2rem; font-weight: 100; }
  h4 { font-size: 1.75rem; font-weight: 300; }
  h5 { font-size: 1.2em; line-height: 1.5em; }
  h6 { font-weight: 500; color: #3e3f42; }

  /* Form controls inherit the body font instead of UA defaults
     (otherwise <input type=submit> renders in tiny system font). */
  input, button, select, optgroup, textarea {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  /* hr default — dskit set border-color to a light gray; without an
     explicit border style, browsers render hr with an inset border that
     looks dark. Set explicitly. */
  hr {
    border: 0;
    border-top: 1px solid #eaedf3;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
`;

const GlobalStyles = () => (
  <>
    <link
      href="https://fonts.googleapis.com/css2?family=Lato:wght@300;400;500;700&family=Montserrat:wght@400;500;700&display=swap"
      rel="stylesheet"
    />
    <CustomStyles />
    <PreflightStyles />
  </>
);

export default GlobalStyles;
